// Simple sizes -> real measurement blueprints.
//
// Turns an off-the-rack selection (jacket size, waist, collar, height, fit) into blueprints using the
// MCC app's own block tables and functions (mcc_blocks.js / measurements-algo.js, loaded on every page by
// the app embed), then saves them to the customer's account through the app's measurements API.
// From there the app's normal flow takes over: auto-assigning patterns to cart lines, writing the
// Pattern / _BlueprintID / _mData line properties at checkout, and unlocking the checkout button.
//
// Guests (not logged in) keep their standard-size blueprints in this browser (localStorage) instead of an
// account. They are handed to the app the same way, so the cart assigns them and writes _mData at checkout;
// the app's orders/create webhook then saves the ordered patterns to the checkout customer. If the guest
// logs in later, sizes for garments their account has no pattern for are moved onto the account.
// A guest who takes the app's custom survey gets their patterns in the same store, and their answers and
// measurements kept under GUEST_SURVEY_KEY and on the cart as the `_mSurvey` attribute (the app's guestFit.js).
(() => {
  const API_URL = 'https://shopify.mccapparel.com/api/customer-measurements';
  const AUTHOR = 'simple';

  const getGlobal = name => {
    try {
      // Block tables are top-level consts in the app's classic scripts: reachable by name, not on window
      // eslint-disable-next-line no-eval
      return typeof window[name] !== 'undefined' ? window[name] : (0, eval)(`typeof ${name} !== 'undefined' ? ${name} : undefined`);
    } catch (error) {
      return undefined;
    }
  };

  const getCustomerId = () => {
    const id =
      window.__SHOPIFY_CUSTOMER__?.id ||
      window.Shopify?.customer?.id ||
      window.Shopify?.customerId ||
      document.querySelector('size-chooser[data-customer-id]')?.dataset.customerId ||
      null;
    return id ? String(id).split('/').pop() : null;
  };

  // ----- Guest sizes (this browser only) -----
  const GUEST_KEY = 'mcc-guest-blueprints';
  const GUEST_SURVEY_KEY = 'mcc-guest-survey';
  const SURVEY_ATTRIBUTE = '_mSurvey';

  const readGuest = () => {
    try {
      const list = JSON.parse(localStorage.getItem(GUEST_KEY) || '[]');
      return Array.isArray(list) ? list.filter(bp => bp && typeof bp === 'object' && bp.product && bp.block) : [];
    } catch (error) {
      return [];
    }
  };

  // Returns false when the browser won't store it (private mode, storage blocked)
  const writeGuest = list => {
    try {
      if (list.length) localStorage.setItem(GUEST_KEY, JSON.stringify(list));
      else localStorage.removeItem(GUEST_KEY);
      return true;
    } catch (error) {
      return false;
    }
  };

  // { formData, measurements } from the app's custom survey, or null
  const readGuestSurvey = () => {
    try {
      const survey = JSON.parse(localStorage.getItem(GUEST_SURVEY_KEY));
      return survey && survey.formData && typeof survey.formData === 'object' ? survey : null;
    } catch (error) {
      return null;
    }
  };

  const clearGuestSurvey = () => {
    try {
      localStorage.removeItem(GUEST_SURVEY_KEY);
    } catch (error) {}
  };

  const loginRequired = () => {
    const error = new Error('Please log in to save your size.');
    error.code = 'LOGIN_REQUIRED';
    return error;
  };

  const newId = () => (Date.now() + Math.random()).toString();

  // Which block tables / functions each garment mode uses
  const MODES = {
    jacket: { sizeKey: 'jacketSize', table: 'simpleJacketBlocks', build: 'getSimpleJacketFinalBlock', fit: 'applySimpleJacketFitModifier', product: 'jacket', label: 'Jacket' },
    vest: { sizeKey: 'jacketSize', table: 'simpleVestBlocks', build: 'getSimpleVestFinalBlock', fit: 'applySimpleVestFitModifier', product: 'vest', label: 'Vest' },
    overcoat: { sizeKey: 'jacketSize', table: 'simpleOvercoatBlocks', build: 'getSimpleOvercoatFinalBlock', fit: 'applySimpleOvercoatFitModifier', product: 'overcoat', label: 'Overcoat' },
    pants: { sizeKey: 'pantsWaist', table: 'simplePantsBlocks', build: 'getSimplePantsFinalBlock', fit: 'applySimplePantsFitModifier', product: 'pants', label: 'Pants' },
    shirt: { sizeKey: 'neckSize', table: 'simpleShirtBlocks', build: 'getSimpleShirtFinalBlock', fit: 'applySimpleShirtFitModifier', product: 'shirt', label: 'Shirt' }
  };
  // A suit is a jacket blueprint plus a pants blueprint
  const expandModes = modes => [...new Set(modes.flatMap(mode => (mode === 'suit' || mode === 'tuxedo' ? ['jacket', 'pants'] : [mode])))].filter(mode => MODES[mode]);

  // Sizes available in the app's block tables (used to build the chooser options)
  const sizesFor = mode => {
    const table = getGlobal(MODES[mode]?.table);
    return Array.isArray(table) ? table.map(row => String(parseFloat(row.Size))).filter(v => v !== 'NaN') : [];
  };
  const heights = () => {
    const table = getGlobal('heightLengthLookup');
    return Array.isArray(table)
      ? table.map(row => row.Height).filter(h => typeof h === 'string' && /^\d+'/.test(h)).map(h => h.replace(/"$/, '')).map(h => (/'$/.test(h) ? `${h}0` : h))
      : [];
  };

  const fitLabel = fit => ({ slim: 'Slim', tailored: 'Tailored', relaxed: 'Relaxed' }[fit] || fit);
  const heightLabel = height => (height ? `${height.replace(/"$/, '')}"` : '');

  // Build one blueprint for a garment mode from the selection
  const buildBlueprint = (mode, selection) => {
    const spec = MODES[mode];
    const build = getGlobal(spec.build);
    const applyFit = getGlobal(spec.fit);
    if (typeof build !== 'function') throw new Error(`Measurement functions are not available (${spec.build})`);

    const size = selection[spec.sizeKey];
    const height = selection.height;
    const fit = selection.fit || 'slim';
    if (!size || !height) return null;

    let block = build(size, height);
    if (!block) throw new Error(`No ${spec.label.toLowerCase()} block for size ${size}`);
    if (typeof applyFit === 'function') block = applyFit(block, fit);

    return {
      id: newId(),
      product: spec.product,
      name: `${spec.label} ${size} · ${fitLabel(fit)}`,
      date: new Date().toISOString(),
      block,
      type: 'blueprint',
      author: AUTHOR,
      generated: true,
      isDefault: true,
      simpleSize: { size, height: heightLabel(height), fit }
    };
  };

  // The API has returned blueprint lists as arrays, JSON strings and id-keyed objects; always hand back an array.
  // Unreadable data throws rather than reading as empty, so a save can't wipe the customer's measurements.
  const toList = value => {
    if (typeof value === 'string') {
      if (!value.trim()) return [];
      try {
        value = JSON.parse(value);
      } catch (error) {
        throw new Error('Could not read saved measurements');
      }
    }
    const list = Array.isArray(value) ? value : value && typeof value === 'object' ? Object.values(value) : [];
    return list.filter(item => item && typeof item === 'object');
  };

  const fetchAccount = async customerId => {
    const response = await fetch(`${API_URL}?shop=${encodeURIComponent(window.Shopify.shop)}&customerId=${encodeURIComponent(customerId)}`, {
      headers: { 'Content-Type': 'application/json' }
    });
    if (!response.ok) throw new Error(`Could not load measurements (${response.status})`);
    const account = (await response.json()) || {};
    return { ...account, blueprints: toList(account.blueprints), blueprints_archive: toList(account.blueprints_archive) };
  };

  const saveAccount = async (customerId, account, blueprints) => {
    const response = await fetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        shop: window.Shopify.shop,
        customerId,
        formData: account.form_data || {},
        measurements: account.measurements || {},
        blueprints,
        blueprintsArchive: account.blueprints_archive
      })
    });
    if (!response.ok) throw new Error(`Could not save measurements (${response.status})`);
    return response.json();
  };

  // Where the current visitor's blueprints live: their account, or this browser for guests
  const storeFor = customerId =>
    customerId
      ? { load: () => fetchAccount(customerId), write: (account, blueprints) => saveAccount(customerId, account, blueprints) }
      : {
          load: async () => ({ blueprints: readGuest(), blueprints_archive: [] }),
          // Guests keep no archive; without storage there is nowhere to keep a guest's size, so ask them to log in
          write: async (account, blueprints) => {
            if (!writeGuest(blueprints)) throw loginRequired();
          }
        };

  // Let the app re-run pattern assignment for the given cart lines with the new blueprint set
  const reassign = (blueprints, variantIds = []) => {
    window.blueprints = blueprints;
    window.lineItemPatterns = window.lineItemPatterns || {};
    variantIds.forEach(id => {
      delete window.lineItemPatterns[id];
    });
    if (typeof window.autoAssignPatternsToCartItems === 'function') {
      try {
        window.autoAssignPatternsToCartItems();
      } catch (error) {
        console.error(error);
      }
    }
    window.isPatternAssignmentComplete = true;
    window.dispatchEvent(new CustomEvent('forceButtonUpdate'));
    document.dispatchEvent(new CustomEvent('simple-size:changed', { detail: { blueprints } }));
  };

  /**
   * Create blueprints for the given garment modes from a selection and save them to the customer.
   * Existing simple-size blueprints for the same garments are replaced; other blueprints lose their
   * default flag for that garment so the new size is what gets assigned.
   */
  const save = async (modes, selection, { variantIds = [] } = {}) => {
    const store = storeFor(getCustomerId());
    const garments = expandModes(modes);
    const created = garments.map(mode => buildBlueprint(mode, selection)).filter(Boolean);
    if (!created.length) throw new Error('Nothing to save for this selection.');

    const account = await store.load();
    const products = new Set(created.map(bp => bp.product));
    const kept = account.blueprints
      .filter(bp => !(bp.author === AUTHOR && products.has(bp.product)))
      .map(bp => (products.has(bp.product) && bp.isDefault ? { ...bp, isDefault: false } : bp));
    const blueprints = [...kept, ...created];

    await store.write(account, blueprints);
    reassign(blueprints, variantIds);
    return created;
  };

  /** Remove simple-size blueprints for the given garment modes and re-run assignment. */
  const clear = async (modes, { variantIds = [] } = {}) => {
    const store = storeFor(getCustomerId());
    const products = new Set(expandModes(modes).map(mode => MODES[mode].product));
    const account = await store.load();
    const current = account.blueprints;
    const blueprints = current.filter(bp => !(bp.author === AUTHOR && products.has(bp.product)));
    if (blueprints.length !== current.length) {
      await store.write(account, blueprints);
    }
    reassign(blueprints, variantIds);
    return blueprints;
  };

  /**
   * "Generate New Patterns": move every blueprint for the given garment modes (standard or custom) to the
   * account's archive, so the customer starts over with no patterns for them, and re-run assignment.
   */
  const archive = async (modes, { variantIds = [] } = {}) => {
    const store = storeFor(getCustomerId());
    const products = new Set(expandModes(modes).map(mode => MODES[mode].product));
    const account = await store.load();
    const now = new Date().toISOString();
    const archived = account.blueprints
      .filter(bp => products.has(bp.product))
      .map(bp => ({ ...bp, archivedDate: now, archiveReason: 'regenerate' }));
    const blueprints = account.blueprints.filter(bp => !products.has(bp.product));
    if (archived.length) {
      await store.write({ ...account, blueprints_archive: [...account.blueprints_archive, ...archived] }, blueprints);
    }
    reassign(blueprints, variantIds);
    return blueprints;
  };

  /** Simple-size blueprints currently on the account (from the app's loaded state) */
  const current = () => (window.blueprints || []).filter(bp => bp.author === AUTHOR);

  /** The saved simple sizes as a size-chooser selection ({ jacketSize, pantsWaist, neckSize, height, fit }) */
  const selection = () => {
    const selected = {};
    current().forEach(bp => {
      const simple = bp.simpleSize || {};
      const key = Object.values(MODES).find(spec => spec.product === bp.product)?.sizeKey;
      if (key && simple.size && !selected[key]) selected[key] = String(simple.size);
      if (simple.height && !selected.height) selected.height = String(simple.height).replace(/"$/, '');
      if (simple.fit && !selected.fit) selected.fit = simple.fit;
    });
    return selected;
  };

  // ----- Find your size -----
  // Answers to step 1 of the MCC custom survey (height, weight, age, T-shirt size, pant size) are scored with
  // the app's own algorithm (measurements-algo.js). The customer gets a standard size only when each garment's
  // score lands inside the block tables and that block has a standard size; anything else needs the survey.

  const inScoreRange = (score, blocks) => {
    if (!Array.isArray(blocks) || !blocks.length || typeof score !== 'number' || isNaN(score)) return false;
    const min = Math.min(...blocks.map(block => parseFloat(block.minScore)));
    const max = Math.max(...blocks.map(block => parseFloat(block.maxScore)));
    // Neighbouring ranges leave 0.01 gaps (40.24 → 40.25); getClosestBlock settles those
    return score >= min - 0.01 && score <= max + 0.01;
  };

  // The standard size whose `key` measurement is nearest `value`, within `tolerance`; ties go to the larger size
  const nearestSize = (table, key, value, tolerance) => {
    let best = null;
    (Array.isArray(table) ? table : []).forEach(row => {
      const distance = Math.abs(parseFloat(row[key]) - value);
      if (isNaN(distance) || distance > tolerance) return;
      if (!best || distance < best.distance || (distance === best.distance && parseFloat(row.Size) > parseFloat(best.row.Size))) {
        best = { row, distance };
      }
    });
    return best ? String(parseFloat(best.row.Size)) : null;
  };

  /**
   * Match survey step 1 answers to standard sizes for the given garment modes.
   * @param {string[]} modes
   * @param {Object} answers  { height, weight, age, upperSize, backupSize, jeanSize, backupJeanSize } in survey format
   * @returns {{ match: true, selection: Object } | { match: false, garment?: string }}
   */
  const find = (modes, answers) => {
    const calculateFitScore = getGlobal('calculateFitScore');
    const getClosestBlock = getGlobal('getClosestBlock');
    const getPantScoreFactor = getGlobal('getPantScoreFactor');
    if ([calculateFitScore, getClosestBlock, getPantScoreFactor].some(fn => typeof fn !== 'function')) return { match: false };

    const knownHeights = heights();
    if (!answers.height || (knownHeights.length && !knownHeights.includes(answers.height))) return { match: false };

    const garments = expandModes(modes);
    const score = garments.some(mode => mode !== 'pants') ? calculateFitScore(answers) : null;
    const selection = { height: answers.height };

    for (const mode of garments) {
      let size = null;
      if (mode === 'pants') {
        const pantScore = getPantScoreFactor(answers.jeanSize, answers.backupJeanSize);
        const blocks = getGlobal('pantsBlocks');
        const block = inScoreRange(pantScore, blocks) ? getClosestBlock(pantScore, blocks) : null;
        if (block) size = nearestSize(getGlobal(MODES.pants.table), 'waist', parseFloat(block.waist), 1);
      } else if (mode === 'shirt') {
        const blocks = getGlobal('shirtBlocks');
        const block = inScoreRange(score, blocks) ? getClosestBlock(score, blocks) : null;
        if (block) size = nearestSize(getGlobal(MODES.shirt.table), 'neck', parseFloat(block.neck), 0.5);
      } else {
        // Jacket, vest and overcoat all come off the jacket block, scored one point lower (as in the survey)
        const blocks = getGlobal('jacketBlocks');
        const block = inScoreRange(score - 1, blocks) ? getClosestBlock(score - 1, blocks) : null;
        if (block && sizesFor(mode).includes(String(parseFloat(block.Size)))) size = String(parseFloat(block.Size));
      }
      if (!size) return { match: false, garment: mode };
      selection[MODES[mode].sizeKey] = size;
    }
    return { match: true, selection };
  };

  // ----- Page load -----

  // A guest's survey goes with their order: put it back on the cart when the cart doesn't carry it
  // (a new cart, or the app couldn't set it). The orders/create webhook saves it to the checkout customer.
  const applyGuestSurvey = async () => {
    const survey = readGuestSurvey();
    if (!survey) return;
    const value = JSON.stringify({ formData: survey.formData, measurements: survey.measurements || {} });
    const root = window.Shopify?.routes?.root || '/';
    try {
      const cart = await (await fetch(`${root}cart.js`)).json();
      if (cart.attributes?.[SURVEY_ATTRIBUTE] === value) return;
      await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { [SURVEY_ATTRIBUTE]: value } })
      });
    } catch (error) {
      console.error('Could not put the guest survey on the cart', error);
    }
  };

  // Hand a guest's saved sizes to the app once its cart script is ready, so cart lines get their patterns
  const applyGuestSizes = () => {
    applyGuestSurvey();
    const guest = readGuest();
    if (!guest.length) return;
    window.blueprints = guest;
    let tries = 0;
    const assign = () => {
      if (typeof window.autoAssignPatternsToCartItems === 'function') reassign(guest);
      else if (++tries < 50) setTimeout(assign, 200);
    };
    assign();
  };

  const isEmpty = value => !value || typeof value !== 'object' || !Object.keys(value).length;

  // A guest who logged in: move their sizes onto the account for garments it has no pattern for yet, and their
  // survey when the account has none. What is already on the account wins, as in the app's order webhook.
  const adoptGuestSizes = async customerId => {
    const guest = readGuest();
    const survey = readGuestSurvey();
    if (!guest.length && !survey) return;
    try {
      const account = await fetchAccount(customerId);
      const savedProducts = new Set(account.blueprints.map(bp => bp.product));
      const toAdd = guest.filter(bp => !savedProducts.has(bp.product));
      // Same shape the app's survey saves: form_data holds the answers and results, measurements the chart
      const addSurvey = Boolean(survey) && isEmpty(account.form_data);
      if (toAdd.length || addSurvey) {
        const blueprints = [...account.blueprints, ...toAdd];
        const updated = addSurvey
          ? { ...account, form_data: { formData: survey.formData, measurementResults: survey.measurements || {} }, measurements: survey.measurements || {} }
          : account;
        await saveAccount(customerId, updated, blueprints);
        reassign(blueprints);
      }
      writeGuest([]);
      clearGuestSurvey();
    } catch (error) {
      // Leave the guest sizes in place to try again on the next page
      console.error('Could not move guest sizes to the account', error);
    }
  };

  const init = () => {
    const customerId = getCustomerId();
    if (customerId) adoptGuestSizes(customerId);
    else applyGuestSizes();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  window.SimpleSize = { save, clear, archive, current, selection, sizesFor, heights, expandModes, getCustomerId, find, MODES, isGuest: () => !getCustomerId() };
})();
